import React, { FC, MouseEvent, ChangeEvent, useState } from "react";

import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Modifier, EditorState, RichUtils } from "draft-js";
import update from "immutability-helper";
import Dropzone, { FileRejection } from "react-dropzone";
import { toast } from "react-toastify";

import "@draft-js-plugins/mention/lib/plugin.css";

import { useBoolean, useApisMembersCompanyFeatureFlagsIndex } from "~/hooks";

import {
  AttachFileType,
  AvatarAndNameEmployeeType,
  EditorStateType,
  PreviewWithFileType,
  RawDraftContentState,
} from "~/domains";

import { ACCEPT_ALL_FILE_TYPES, FILE_MAX_BYTE_SIZE } from "~/constants/file";

import {
  Icon,
  Label,
  ModalWrapper,
  DropzoneFileField,
  ButtonWithIcon,
  TextArea,
} from "~/ui";
import { Editor, PostedFilePreviews } from "~/ui";

type PropsType = {
  labelText?: string;
  required?: boolean;
  mentions: AvatarAndNameEmployeeType[];
  files: PreviewWithFileType[];
  onChangeFiles?: (files: PreviewWithFileType[]) => void;
  editorState: EditorStateType;
  readOnly?: boolean;
  blockQuote?: {
    content: RawDraftContentState;
    employee: AvatarAndNameEmployeeType;
    files: AttachFileType[];
    createdAt: string;
  };
  setEditorState: (editorState: EditorStateType) => void;
  submitButton?: {
    onSubmit: (e: MouseEvent<HTMLButtonElement>) => void;
    isSubmitting: boolean;
  };
  className?: string;
  defaultAutoFucus?: boolean;
  canFileInText?: boolean;
  forBottom?: boolean;
  isEmojiPickerTop?: boolean;
  isCompactSize?: boolean;
  placeholder?: string;
  // TextAreaモード用: 外部からplain textを制御するためのprops
  plainTextValue?: string;
  onPlainTextChange?: (value: string) => void;
};

const MAX_FILES = 4;

export const TextEditor: FC<PropsType> = ({
  labelText = "",
  required = false,
  mentions,
  files = [],
  onChangeFiles,
  editorState,
  readOnly = false,
  blockQuote,
  setEditorState,
  submitButton,
  className = "",
  defaultAutoFucus = false,
  canFileInText = false,
  forBottom = false,
  isEmojiPickerTop = false,
  placeholder = "",
  plainTextValue,
  onPlainTextChange,
}: PropsType) => {
  const { data: featureFlagsData } = useApisMembersCompanyFeatureFlagsIndex();
  const useTextAreaForm =
    featureFlagsData?.features.find((f) => f.key === "use_textarea_form")
      ?.enabled || false;

  // TextAreaモード用の内部state（外部制御がない場合のみ使用）
  const [internalTextAreaValue, setInternalTextAreaValue] = useState("");

  // TextAreaモードで使用する値: 外部制御があればそれを使い、なければ内部stateを使う
  const textAreaValue =
    plainTextValue !== undefined ? plainTextValue : internalTextAreaValue;

  const emojiPickerOpen = useBoolean(false);
  const onHandleImageDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => {
    if (!onChangeFiles) return;

    if (acceptedFiles.length + files.length > MAX_FILES) {
      toast.error("画像は4枚まで貼付可能です");
      return;
    }
    if (fileRejections.length) {
      fileRejections.map(({ file, errors }) => {
        if (!errors[0]) return;
        toast.error(`${file.name} - ${errors[0].message}`);
      });
      return;
    }
    const newFiles = acceptedFiles.map((file) =>
      Object.assign(file, { previewPath: URL.createObjectURL(file) }),
    );
    onChangeFiles(update(files, { $push: newFiles }));
  };

  const handleToggleClick = (
    e: MouseEvent<HTMLButtonElement>,
    inlineStyle: string,
  ) => {
    e.preventDefault();
    setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const handleToggleBlockType = (
    e: MouseEvent<HTMLButtonElement>,
    blockStyle: string,
  ) => {
    e.preventDefault();

    setEditorState(RichUtils.toggleBlockType(editorState, blockStyle));
  };

  const handleMention = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!mentions.length) return;

    addText(" @");
  };

  const addText = (text: string) => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const modifierText = Modifier.insertText(contentState, selection, text);
    const nextEditorState = EditorState.push(
      editorState,
      modifierText,
      "insert-characters",
    );
    setEditorState(nextEditorState);
  };

  const removeFile = (fileIndex: number) => {
    if (!onChangeFiles) return;

    const file = files[fileIndex];
    if (!file) return;
    URL.revokeObjectURL(file.previewPath);
    onChangeFiles(update(files, { $splice: [[fileIndex, 1]] }));
  };

  const handleEmojiModalToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    emojiPickerOpen.toggle();
  };

  // TextAreaモード用のシンプルな変更ハンドラー
  // editorStateを一切使用せず、エラーも発生しない
  const handleTextAreaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // 外部制御があればコールバックを呼び出し、なければ内部stateを更新
    if (onPlainTextChange) {
      onPlainTextChange(value);
    } else {
      setInternalTextAreaValue(value);
    }
  };

  const isDisabled =
    submitButton?.isSubmitting || !editorState.getCurrentContent().hasText();

  // ============================================
  // 一時的な対応: TextEditorが動かない環境向け
  // ============================================
  // use_textarea_form フラグが有効な場合、リッチテキストエディタの代わりに
  // シンプルなTextAreaコンポーネントを表示します。
  // これはTextEditor（Draft.js）が正常に動作しない環境での一時的な回避策です。
  // 将来的には削除される予定のコードです。
  //
  // 【重要】TextAreaモードでは、editorStateを一切使用しません。
  // これにより、Draft.jsのエラーを完全に回避できます。
  //
  // 【使用例】ページでplain textを保存する場合:
  //
  // const [plainText, setPlainText] = useState("");
  //
  // const handleSubmit = () => {
  //   const body = {
  //     // TextAreaモードの場合はplainTextを使用
  //     contentText: plainText,
  //     // その他のデータ
  //   };
  //   // APIへ送信
  // };
  //
  // <InsightPostForm
  //   // 既存のprops...
  //   plainTextValue={plainText}
  //   onPlainTextChange={setPlainText}
  // />
  //
  if (useTextAreaForm) {
    const isTextAreaDisabled =
      submitButton?.isSubmitting || !textAreaValue.trim();

    return (
      <div className={className}>
        {labelText && (
          <Label
            labelText={labelText}
            required={required}
            htmlFor="contentText"
          />
        )}
        <div className="relative">
          <TextArea
            name="contentText"
            value={textAreaValue}
            onChange={handleTextAreaChange}
            placeholder={placeholder}
            required={required}
            disabled={readOnly}
            className="border-secondary-400"
          />
          {submitButton && !readOnly && (
            <div className="flex justify-end mt-2">
              <ButtonWithIcon
                srOnlyText="保存する"
                disabled={isTextAreaDisabled}
                onClick={submitButton.onSubmit}
                withHoverEffect
                icon={{
                  icon: "ioSend",
                  size: "1.5rem",
                  className: `${isTextAreaDisabled ? "cursor-not-allowed" : ""}`,
                  color: isTextAreaDisabled
                    ? "text-secondary-400"
                    : "text-primary-600",
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
  // ============================================

  // 既存のTextEditor（リッチエディタ）を使用する場合
  return (
    <div className={className}>
      {labelText && (
        <Label
          labelText={labelText}
          required={required}
          htmlFor="contentText"
        />
      )}
      <DropzoneFileField
        noClick
        onDropFiles={onHandleImageDrop}
        acceptFileTypes={ACCEPT_ALL_FILE_TYPES}
        multiple
        maxFileByte={FILE_MAX_BYTE_SIZE}
        maxFiles={MAX_FILES}
        readonly={readOnly}
      >
        <div
          className={`${readOnly ? "bg-secondary-300" : "bg-white cursor-text"} ${
            labelText && "mt-1.5"
          } ${
            forBottom ? "forBottom min-h-[5rem]" : "min-h-[9rem]"
          } border border-secondary-400 border-solid rounded pt-2 pb-1 px-3`}
        >
          <div className="w-full">
            <Editor
              editorState={editorState}
              onChange={setEditorState}
              readOnly={readOnly}
              mentions={mentions}
              required={required}
              canFileInText={canFileInText}
              defaultAutoFucus={defaultAutoFucus}
              blockQuote={blockQuote}
              placeholder={placeholder}
            />
          </div>
          {Boolean(files.length) && (
            <PostedFilePreviews
              files={files.map((file) => ({
                name: file.name,
                url: file.previewPath,
                contentType: file.type,
              }))}
              removeFileIndex={removeFile}
              className="mt-2"
            />
          )}
          {!readOnly && (
            <>
              <div className="flex items-center justify-end mt-4 pt-1 space-x-2 border-t">
                {Boolean(onChangeFiles) && (
                  <>
                    <Dropzone
                      onDrop={onHandleImageDrop}
                      accept={ACCEPT_ALL_FILE_TYPES}
                      multiple
                      maxSize={FILE_MAX_BYTE_SIZE}
                      maxFiles={MAX_FILES}
                    >
                      {({ getRootProps, getInputProps }) => (
                        <div
                          className="cursor-pointer p-1.5 rounded-full hover:bg-secondary-200"
                          {...getRootProps()}
                        >
                          <input {...getInputProps()} />
                          <Icon
                            icon="ioDuplicateOutline"
                            size="1.5rem"
                            color="text-primary-600"
                          />
                        </div>
                      )}
                    </Dropzone>
                    <span className="mx-1 h-5 w-px bg-secondary-300" />
                  </>
                )}
                {Boolean(mentions.length) && (
                  <>
                    <ButtonWithIcon
                      srOnlyText="メンションを追加する"
                      withHoverEffect
                      onClick={handleMention}
                      icon={{
                        icon: "ioAtOutline",
                        size: "1.5rem",
                        color: "text-primary-600",
                      }}
                    />
                    <span className="mx-1 h-5 w-px bg-secondary-300" />
                  </>
                )}
                <ButtonWithIcon
                  srOnlyText="太字にする"
                  onClick={(e) => handleToggleClick(e, "BOLD")}
                  withHoverEffect
                  icon={{
                    icon: "fiBold",
                    size: "1.5rem",
                    color: "text-primary-600",
                  }}
                />
                <span className="mx-1 h-5 w-px bg-secondary-300" />
                <ButtonWithIcon
                  srOnlyText="文字を大きくする"
                  onClick={(e) => handleToggleBlockType(e, "header-one")}
                  withHoverEffect
                  icon={{
                    icon: "ioTextOutline",
                    size: "1.5rem",
                    color: "text-primary-600",
                  }}
                />
                <span className="mx-1 h-5 w-px bg-secondary-300" />
                <ButtonWithIcon
                  srOnlyText="絵文字を追加する"
                  withHoverEffect
                  onClick={handleEmojiModalToggle}
                  icon={{
                    icon: "ioHappyOutline",
                    size: "1.5rem",
                    color: "text-primary-600",
                  }}
                />
                <span className="mx-1 h-5 w-px bg-secondary-300" />
                {submitButton && (
                  <ButtonWithIcon
                    srOnlyText="保存する"
                    disabled={isDisabled}
                    onClick={submitButton.onSubmit}
                    withHoverEffect
                    icon={{
                      icon: "ioSend",
                      size: "1.5rem",
                      className: `${isDisabled ? "cursor-not-allowed" : ""}`,
                      color: isDisabled
                        ? "text-secondary-400"
                        : "text-primary-600",
                    }}
                  />
                )}
              </div>
              {emojiPickerOpen.isChecked && (
                <>
                  <ModalWrapper isWhite onClose={emojiPickerOpen.setFalse} />
                  <div className="relative">
                    <div
                      className={`flex justify-end absolute z-modal right-0 ${
                        isEmojiPickerTop ? "bottom-10" : "-top-1"
                      }`}
                    >
                      <Picker
                        data={data}
                        onEmojiSelect={(emoji: { native: string }) =>
                          addText(` ${emoji.native} `)
                        }
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </DropzoneFileField>
    </div>
  );
};
